package one.primehealth.analytics.impl

import co.touchlab.kermit.Logger
import dev.gitlive.firebase.functions.FirebaseFunctions
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.launch
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import one.healthium.Platform
import one.healthium.base.coroutines.IO
import one.healthium.base.preferences.AppPreferences
import one.healthium.domain.user.UserInteractor
import one.primehealth.analytics.AppAnalytics
import one.primehealth.analytics.events.AppEvent
import one.primehealth.app.BaseAppConfig
import kotlin.time.Clock
import kotlin.time.Instant
import kotlin.uuid.Uuid

internal class HltmAnalytics(
    private val platform: Platform,
    private val appPreferences: AppPreferences,
    private val baseAppConfig: BaseAppConfig,
    private val functions: FirebaseFunctions,
    private val userInteractor: UserInteractor,
    private val hltmUserMapper: HltmUserMapper,
) : AppAnalytics {

    private val events = mutableListOf<Pair<Instant, AppEvent>>() // TODO persist?
    private var currentUserProfile = emptyMap<String, Any?>()
    private val scope = CoroutineScope(Dispatchers.IO + SupervisorJob())
    private val eventsMutex = Mutex()

    init {
        scope.launch {
            userInteractor.observeUser()
                .catch { Logger.e("Error observing user for HLTM", it) }
                .collect { user ->
                    eventsMutex.withLock {
                        currentUserProfile = hltmUserMapper.map(user)
                    }
                }
        }
    }

    override fun logEvent(event: AppEvent) {
        scope.launch {
            try {
                eventsMutex.withLock {
                    events.add(Clock.System.now() to event)
                }
                dispatchWorker()
            } catch (e: Exception) {
                Logger.e("Error logging event to HLTM: ${event.name}", e)
            }
        }
    }

    private fun dispatchWorker() {
        scope.launch {
            try {
                eventsMutex.withLock {
                    if (events.size >= 10 || events.any { it.second.critical }) {
                        sendEvents(events)
                        events.clear()
                    }
                }
            } catch (e: Exception) {
                Logger.e("Error dispatching events to HLTM", e)
            }
        }
    }

    private suspend fun sendEvents(events: List<Pair<Instant, AppEvent>>) {
        val data = mapOf(
            "events" to events.map { (time, event) ->
                val user = currentUserProfile + event.userProperties + ("puid" to getUserPuid())

                buildMap {
                    put("event_id", generateEventId(time))
                    put("event", event.name)
                    put("puid", getUserPuid())
                    event.properties
                        ?.plus("font_scale_factor" to platform.deviceInfo.fontScaleFactor)
                        ?.takeIf { it.isNotEmpty() }
                        ?.let { put("params", it) }
                    put("user", user)
                    put("timestamp_ms", time.toEpochMilliseconds())
                }
            },
            "system" to system,
            "timestamp_ms" to Clock.System.now().toEpochMilliseconds(),
        )
        Logger.d("Sending events to HLTM: $data")
        functions.httpsCallable("hltm_sendevent").invoke(data)
    }

    private fun getUserPuid(): String {
        val userPUID = appPreferences.userPUID
        return if (userPUID.isNullOrEmpty()) {
            val newUserPUID = generateUserPuid()
            appPreferences.userPUID = newUserPUID
            newUserPUID
        } else {
            userPUID
        }
    }

    private fun generateUserPuid(): String {
        return "${baseAppConfig.appId}_${Uuid.random()}"
    }

    private fun generateEventId(time: Instant): String {
        val chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*()_+-="
        return "${baseAppConfig.appId}_${time.toEpochMilliseconds()}_${chars.random()}${chars.random()}${chars.random()}"
    }

    private val system = mapOf(
        "platform" to "app",
        "device" to platform.deviceInfo.device,
        "device_family" to platform.deviceInfo.deviceFamily,
        "os" to platform.deviceInfo.os,
        "os_version" to platform.deviceInfo.osVersion,
        "language" to platform.deviceInfo.language,
    )
}